/**
 * @fileoverview Custom hook and utility for scrolling to top on route changes.
 */

import {ReactElement, useEffect} from "react";
import {useLocation, useNavigationType} from "react-router-dom";

/**
 * Custom hook that scrolls to the top of a specified element when the route changes.
 * Only handles PUSH and REPLACE navigation actions.
 *
 * @param {Object} params The hook parameters.
 * @param {string} params.selector CSS selector for the element to scroll to top.
 */
export const useScrollToTopOnHistoryChange = ({
  selector,
}: {
  selector: string;
}) => {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "PUSH" || navigationType === "REPLACE") {
      requestAnimationFrame(() => scrollToTop(selector));
    }
  }, [location, navigationType, selector]);
};

/**
 * Component that scrolls to top on route changes.
 * Uses the useScrollToTopOnHistoryChange hook internally.
 *
 * @param {Object} props The component props.
 * @param {string} props.selector CSS selector for the element to scroll to top.
 * @param {ReactElement} [props.children=null] Optional children to render.
 * @returns {ReactElement} The rendered children.
 */
export const UseScrollToTopOnHistoryChange = ({
  selector,
  children = null,
}: {
  selector: string;
  children?: ReactElement;
}) => {
  useScrollToTopOnHistoryChange({selector});

  return children;
};

/**
 * Utility function that scrolls an element to the top.
 * Uses the element's scrollTop property to scroll to position 0.
 *
 * @param {string} [selector="html"] CSS selector for the element to scroll to top.
 */
export const scrollToTop = (selector = "html") => {
  const element = document.querySelector(selector);

  if (element) {
    element.scrollTop = 0;
  }
};
